import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { CardManager } from "./HomePage";
import { manageSeo, privateAreaSeo, setPageSeo } from "../seo";
import cardsWhiteLogoUrl from "../assets/Logo - Cards - white.svg";
import { trackEvent } from "../analytics";

const managementFaqs = [
  [
    "Come accedo alla gestione?",
    "Inserisci l'email collegata alla tua Cards. Se il profilo è presente, riceverai una mail con il link di accesso e il codice OTP.",
  ],
  [
    "Non ho ancora ricevuto il codice?",
    "Controlla anche spam o posta indesiderata. Se dopo qualche minuto non arriva, puoi richiedere un nuovo codice dalla stessa pagina.",
  ],
  [
    "Posso creare una nuova Cards da qui?",
    "Questa pagina serve per accedere e modificare una Cards già collegata alla tua email. Per aprire un nuovo profilo torna alla homepage e usa la sezione di iscrizione.",
  ],
];

const authenticatedTips = [
  [
    "Aggiorna i dati pubblici",
    "Modifica recapiti, ruolo, impresa e informazioni visibili nella tua Cards.",
  ],
  [
    "Salva e controlla online",
    "Dopo il salvataggio apri la card pubblica per verificare cosa vedono i tuoi contatti.",
  ],
  [
    "Tieni l'email corretta",
    "Se cambi l'email personale, potremmo chiederti una nuova verifica OTP per sicurezza.",
  ],
];

const aiCreditFaqs = [
  [
    "A cosa servono i crediti AI?",
    "I crediti AI possono essere usati per generare avatar professionali da usare nella tua Cards.",
  ],
  [
    "Quanti crediti sono inclusi?",
    "Ogni profilo include 3 crediti AI iniziali. Quando terminano, puoi acquistare nuovi crediti una tantum, senza abbonamenti aggiuntivi.",
  ],
];

function ManageCardPage() {
  const location = useLocation();
  const initialEmail = location.state?.initialEmail || "";
  const [isAuthenticatedCardView, setIsAuthenticatedCardView] = useState(false);

  useEffect(() => {
    setPageSeo(
      isAuthenticatedCardView || location.search
        ? privateAreaSeo
        : manageSeo,
    );
  }, [isAuthenticatedCardView, location.search]);

  return (
    <div className="min-h-screen bg-[#e8eef2] font-Poppins text-slate-950">
      <header className="bg-[#17465c] text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-3 py-4 sm:px-5">
          <a href="/" className="flex items-center">
            <img
              src={cardsWhiteLogoUrl}
              alt="Cards"
              className="h-9 w-auto"
            />
          </a>
          <a
            href="/"
            className="rounded-md bg-white/10 px-3 py-2 text-xs font-bold text-white ring-1 ring-white/20 transition hover:bg-white/15"
          >
            Homepage
          </a>
        </div>
      </header>

      <main className="px-3 py-6 sm:px-5 sm:py-12">
        <div className="mx-auto max-w-6xl">
          {isAuthenticatedCardView && (
            <div className="mb-6">
              <span className="rounded-full bg-cyan-100 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#13526f]">
                Area personale
              </span>
              <h1 className="mt-4 text-2xl font-extrabold leading-tight text-slate-950 sm:text-4xl">
                Gestisci la tua Cards
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                Aggiorna i dati della tua card, salva le modifiche e riapri la
                Cards per vedere online le informazioni aggiornate.
              </p>
            </div>
          )}

          <div
            className={
              isAuthenticatedCardView
                ? "grid gap-6"
                : "grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.38fr)] lg:items-start"
            }
          >
            {isAuthenticatedCardView && (
              <section className="order-2 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.4fr)]">
                <div className="rounded-xl bg-white p-4 shadow-lg shadow-slate-950/5 ring-1 ring-slate-200 sm:p-5">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
                      <Icon
                        icon="mdi:card-account-details-star-outline"
                        className="text-2xl"
                      />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
                        Gestione attiva
                      </p>
                      <h2 className="text-lg font-extrabold text-slate-950">
                        Cosa puoi fare ora
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-slate-600">
                        Usa questi promemoria mentre aggiorni la card.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 lg:grid-cols-3">
                    {authenticatedTips.map(([title, text]) => (
                      <div
                        key={title}
                        className="rounded-lg bg-slate-50 px-4 py-3 ring-1 ring-slate-200"
                      >
                        <p className="text-sm font-extrabold text-slate-800">
                          {title}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-slate-600">
                          {text}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {aiCreditFaqs.map(([question, answer]) => (
                      <div
                        key={question}
                        className="rounded-lg bg-cyan-50/70 px-4 py-3 ring-1 ring-cyan-100"
                      >
                        <p className="inline-flex items-center gap-2 text-sm font-extrabold text-slate-800">
                          <Icon
                            icon="mdi:sparkles"
                            className="text-base text-[#1b8395]"
                          />
                          {question}
                        </p>
                        <p className="mt-2 text-xs leading-5 text-slate-600">
                          {answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl bg-[#123d54] p-4 text-white shadow-lg shadow-slate-950/5 sm:p-5">
                  <p className="text-xs font-bold uppercase tracking-[0.08em] text-cyan-100">
                    Supporto
                  </p>
                  <p className="mt-2 text-sm leading-6 text-cyan-50/80">
                    Hai dubbi sui dati da aggiornare, sul piano attivo o sulla
                    pubblicazione della tua Cards?
                  </p>
                  <a
                    href="mailto:cards@onoranzefunebricloud.com?subject=Supporto%20gestione%20Cards"
                    onClick={() =>
                      trackEvent("self_service_support_click", {
                        category: "SelfService",
                        authenticated: true,
                      })
                    }
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-white px-4 py-3 text-sm font-extrabold text-[#13526f] transition hover:bg-cyan-50"
                  >
                    <Icon icon="mdi:email-fast-outline" className="text-lg" />
                    Scrivi al supporto
                  </a>
                  <p className="mt-3 break-words text-xs font-semibold text-cyan-50/70">
                    cards@onoranzefunebricloud.com
                  </p>
                </div>
              </section>
            )}

            <div className={isAuthenticatedCardView ? "order-1" : ""}>
              <CardManager
                initialEmail={initialEmail}
                startWithMaskedEmail={Boolean(initialEmail)}
                onAuthenticatedViewChange={setIsAuthenticatedCardView}
              />
            </div>

            {!isAuthenticatedCardView && (
              <aside className="space-y-4">
                <div className="rounded-xl bg-white p-5 shadow-xl shadow-slate-950/10 ring-1 ring-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-cyan-50 text-[#1b8395]">
                      <Icon
                        icon="mdi:help-circle-outline"
                        className="text-2xl"
                      />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
                        Aiuto accesso
                      </p>
                      <h2 className="text-lg font-extrabold text-slate-950">
                        Domande rapide
                      </h2>
                    </div>
                  </div>

                  <div className="mt-5 space-y-3">
                    {managementFaqs.map(([question, answer]) => (
                      <details
                        key={question}
                        className="group rounded-lg bg-slate-50 px-4 py-3 ring-1 ring-slate-200"
                      >
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-extrabold text-slate-800">
                          {question}
                          <Icon
                            icon="mdi:chevron-down"
                            className="shrink-0 text-lg text-slate-400 transition group-open:rotate-180"
                          />
                        </summary>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          {answer}
                        </p>
                      </details>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl bg-[#123d54] p-5 text-white shadow-xl shadow-slate-950/10">
                  <p className="text-xs font-bold uppercase tracking-[0.08em] text-cyan-100">
                    Supporto
                  </p>
                  <p className="mt-2 text-sm leading-6 text-cyan-50/80">
                    Hai dubbi sull'accesso, sull'email associata alla card o sul
                    codice OTP?
                  </p>
                  <a
                    href="mailto:cards@onoranzefunebricloud.com?subject=Supporto%20gestione%20Cards"
                    onClick={() =>
                      trackEvent("self_service_support_click", {
                        category: "SelfService",
                        authenticated: false,
                      })
                    }
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-white px-4 py-3 text-sm font-extrabold text-[#13526f] transition hover:bg-cyan-50"
                  >
                    <Icon icon="mdi:email-fast-outline" className="text-lg" />
                    Scrivi al supporto
                  </a>
                  <p className="mt-3 break-words text-xs font-semibold text-cyan-50/70">
                    cards@onoranzefunebricloud.com
                  </p>
                </div>
              </aside>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default ManageCardPage;
